import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { UserCurrencyTransaction } from '../../../../../models/users/user-currency-transaction.model';
import { UserCurrencyTransactionType } from '../../../../../enums/users/user-currency-transaction-type.enum';
import { UserCurrencyType } from '../../../../../enums/users/user-currency-type.enum';
import { UserTransactionsFiltersRequest } from '../../../../../models/users/requests/api-users-get-user-transactions-request.model';
import { UserTransactionsResultsResponse } from '../../../../../models/users/responses/api-users-transactions-results-response.model';
import { ApiUsersService } from '../../../../../services/users/api/api-users.service';
import { AlertsService } from '../../../../../services/alerts.service';
import { DateService } from '../../../../../services/date.service';

@Component({
  selector: 'app-user-transactions-tab',
  templateUrl: './user-transactions-tab.component.html',
  standalone: true,
  imports: [CollapseModule, FormsModule, CommonModule]
})
export class UserTransactionsTabComponent {

  @Input() userId: number = 0;

  protected readonly UserCurrencyTransactionType = UserCurrencyTransactionType;
  protected readonly UserCurrencyType = UserCurrencyType;
  protected readonly isNaN = isNaN;

  isLoading: boolean = false;
  isFilterOpen: boolean = false;

  transactions: UserCurrencyTransaction[] = [];
  resultsPerPage: number = 25;
  pageNumber: number = 1;
  totalResults: number = 0;

  requestFilters: UserTransactionsFiltersRequest = new UserTransactionsFiltersRequest();

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;

    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);

    const sorted = Array.from(visibleSet).sort((a, b) => a - b);

    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }

    return result;
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.refreshTransactionsList();
  }

  constructor(public dateService: DateService, private apiUsersService: ApiUsersService, private alertsService: AlertsService) {}

  load(): void {
    this.pageNumber = 1;
    this.refreshTransactionsList();
  }

  onClickFilter(): void {
    this.pageNumber = 1;
    this.refreshTransactionsList();
  }

  onClickResetFilters(): void {
    this.requestFilters = new UserTransactionsFiltersRequest();
    this.pageNumber = 1;
    this.refreshTransactionsList();
  }

  onClickPaginationPrevious(): void {
    this.pageNumber--;
    this.refreshTransactionsList();
  }

  onClickPaginationNext(): void {
    this.pageNumber++;
    this.refreshTransactionsList();
  }

  private refreshTransactionsList(): void {
    this.isLoading = true;

    this.apiUsersService.getUserTransactions(this.userId, this.pageNumber, this.resultsPerPage, this.requestFilters).then(
      (response: UserTransactionsResultsResponse) => {
        this.isLoading = false;
        this.transactions = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading transactions (' + error + ')');
      }
    );
  }
}
